import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Paper,
  FormHelperText,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetCouponsDocument,
  CreateCouponDocument,
  MainCouponDocument,
  UpdateMainCouponDocument,
  DeleteMainCouponDocument,
  type GetCouponsQuery,
  type CouponType,
  type CreateCouponMutation,
  type CreateCouponMutationVariables,
  type CouponStatus,
  type CouponUsageType,
  type CouponDiscountType,
  type UpdateMainCouponMutation,
  type UpdateMainCouponMutationVariables,
  type DeleteMainCouponMutation,
} from '@/generated/root-types';
import { DataTable } from '@gastrosoft/shared/ui';
import { useNotification } from '@/app/contexts/NotificationContext';
import { useAuth } from '@/app/auth/AuthContext';
import { isSuperAdmin } from '@/utils/roles';
import { GRAPHQL_MAX_PAGE_SIZE } from '@/utils/constants';
import {
  COUPON_DISCOUNT_TYPES,
  COUPON_STATUSES,
  COUPON_TYPES,
  COUPON_USAGE_TYPES,
  couponDiscountTypeLabel,
  couponStatusLabel,
  couponTypeLabel,
  couponUsageTypeLabel,
  formatCouponDiscount,
  parseCsvList,
} from '@/utils/couponLabels';

interface Row extends Record<string, unknown> {
  id: string;
  code: string;
  name: string;
  description: string | null;
  type: CouponType;
  duration: number | null;
  discountType: CouponDiscountType;
  discountValue: number;
  status: CouponStatus;
  usageType: CouponUsageType;
  maxRedemptions: number | null;
  redemptionCount: number;
  expiryDate: string | null;
  createdAt: string;
  updatedAt: string;
}

const emptyCreateForm = () => ({
  name: '',
  code: '',
  type: 'DURATION' as CouponType,
  discountType: 'PERCENTAGE' as CouponDiscountType,
  discountValue: '10',
  duration: '1',
  description: '',
  maxRedemptions: '',
  expiryDate: '',
  allowedPlanCodes: '',
  allowedProductSkus: '',
  status: 'ACTIVE' as CouponStatus,
  usageType: 'INTERNAL' as CouponUsageType,
});

export default function Coupons() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDate = (iso: string) => new Date(iso).toLocaleString(locale);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const superAdmin = isSuperAdmin(user);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCreateForm);
  const [mainCouponId, setMainCouponId] = useState('');

  const { data, loading, refetch } = useQuery<GetCouponsQuery>(GetCouponsDocument, {
    variables: { first: GRAPHQL_MAX_PAGE_SIZE },
  });

  const { data: mainCouponData, refetch: refetchMainCoupon } = useQuery(MainCouponDocument);

  const [createCoupon, { loading: creating }] = useMutation<
    CreateCouponMutation,
    CreateCouponMutationVariables
  >(CreateCouponDocument);

  const [updateMainCoupon, { loading: savingMainCoupon }] = useMutation<
    UpdateMainCouponMutation,
    UpdateMainCouponMutationVariables
  >(UpdateMainCouponDocument);

  const [deleteMainCoupon, { loading: deletingMainCoupon }] =
    useMutation<DeleteMainCouponMutation>(DeleteMainCouponDocument);

  const savingMain = savingMainCoupon || deletingMainCoupon;

  const nodes = data?.coupons?.nodes ?? [];

  useEffect(() => {
    const current = mainCouponData?.mainCoupon?.id ?? '';
    setMainCouponId(current);
  }, [mainCouponData?.mainCoupon?.id]);

  const rows: Row[] = nodes.map((n) => ({
    id: n.id,
    code: n.code,
    name: n.name,
    description: n.description ?? null,
    type: n.type,
    duration: n.duration ?? null,
    discountType: n.discountType,
    discountValue: n.discountValue,
    status: n.status,
    usageType: n.usageType,
    maxRedemptions: n.maxRedemptions,
    redemptionCount: n.redemptionCount,
    expiryDate: n.expiryDate,
    createdAt: n.createdAt,
    updatedAt: n.updatedAt,
  }));

  const columns: GridColDef[] = [
    { field: 'code', headerName: t('masterData.code'), flex: 1, minWidth: 120 },
    { field: 'name', headerName: t('common.name'), flex: 1, minWidth: 140 },
    {
      field: 'description',
      headerName: t('common.description'),
      flex: 1,
      minWidth: 140,
      valueFormatter: (v: string | null) => (v && v.length > 60 ? `${v.slice(0, 60)}…` : v || '–'),
    },
    {
      field: 'type',
      headerName: t('common.type'),
      width: 140,
      valueFormatter: (v: string) => couponTypeLabel(t, v),
    },
    {
      field: 'discountType',
      headerName: t('coupons.discountType'),
      width: 130,
      valueFormatter: (v: string) => couponDiscountTypeLabel(t, v),
    },
    {
      field: 'discountValue',
      headerName: t('coupons.discountValue'),
      width: 110,
      valueGetter: (_v, row) =>
        formatCouponDiscount(t, row.discountType, row.discountValue, locale),
    },
    {
      field: 'duration',
      headerName: t('coupons.duration'),
      width: 90,
      valueFormatter: (v: number | null) => (v != null ? String(v) : '–'),
    },
    {
      field: 'status',
      headerName: t('common.status'),
      width: 100,
      valueFormatter: (v: string) => couponStatusLabel(t, v),
    },
    {
      field: 'usageType',
      headerName: t('coupons.usageType'),
      width: 110,
      valueFormatter: (v: string) => couponUsageTypeLabel(t, v),
    },
    {
      field: 'redemptionCount',
      headerName: t('coupons.redemptionCount'),
      width: 110,
    },
    {
      field: 'createdAt',
      headerName: t('common.created'),
      width: 150,
      valueFormatter: (v: string) => formatDate(v),
    },
  ];

  const resetForm = () => setForm(emptyCreateForm());

  const handleCreate = async () => {
    const discountValue = parseFloat(form.discountValue.replace(',', '.'));
    if (Number.isNaN(discountValue) || discountValue <= 0) {
      showError(t('errors.generic'));
      return;
    }
    const duration = form.type === 'DURATION' ? parseInt(form.duration, 10) : undefined;
    if (
      form.type === 'DURATION' &&
      (duration === undefined || Number.isNaN(duration) || duration < 1)
    ) {
      showError(t('errors.generic'));
      return;
    }
    const maxRedemptions = form.maxRedemptions.trim()
      ? parseInt(form.maxRedemptions, 10)
      : undefined;
    if (
      form.maxRedemptions.trim() &&
      (maxRedemptions === undefined || Number.isNaN(maxRedemptions))
    ) {
      showError(t('errors.generic'));
      return;
    }

    try {
      const res = await createCoupon({
        variables: {
          input: {
            name: form.name.trim(),
            code: form.code.trim(),
            type: form.type,
            discountType: form.discountType,
            discountValue,
            status: form.status,
            usageType: form.usageType,
            description: form.description.trim() || undefined,
            duration,
            maxRedemptions,
            expiryDate: form.expiryDate ? new Date(form.expiryDate).toISOString() : undefined,
            allowedPlanCodes: parseCsvList(form.allowedPlanCodes),
            allowedProductSkus: parseCsvList(form.allowedProductSkus),
          },
        },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      if (!res.data?.createCoupon?.coupon) {
        showError(t('errors.generic'));
        return;
      }
      showSuccess(t('masterData.created'));
      setOpen(false);
      resetForm();
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const handleSaveMainCoupon = async () => {
    try {
      if (!mainCouponId) {
        const res = await deleteMainCoupon();
        const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
        if (top?.length) {
          showError(top.map((e) => e.message).join(' '));
          return;
        }
        if (!res.data?.deleteMainCoupon?.boolean) {
          showError(t('errors.generic'));
          return;
        }
      } else {
        const res = await updateMainCoupon({
          variables: { input: { id: mainCouponId } },
        });
        const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
        if (top?.length) {
          showError(top.map((e) => e.message).join(' '));
          return;
        }
        if (!res.data?.updateMainCoupon?.boolean) {
          showError(t('errors.generic'));
          return;
        }
      }
      showSuccess(t('coupons.mainCouponUpdated'));
      await refetchMainCoupon();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const mainCouponChanged = mainCouponId !== (mainCouponData?.mainCoupon?.id ?? '');

  return (
    <Box
      sx={{
        height: 'calc(100vh - 180px)',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
          px: { xs: 1, sm: 0 },
        }}
      >
        <Typography variant="h4">{t('coupons.title')}</Typography>
        {superAdmin && (
          <Button variant="contained" onClick={() => setOpen(true)}>
            {t('coupons.create')}
          </Button>
        )}
      </Box>

      {superAdmin && (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: 2,
            mx: { xs: 1, sm: 0 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'flex-end' },
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 280 } }}>
            <TextField
              select
              label={t('coupons.mainCoupon')}
              value={mainCouponId}
              onChange={(e) => setMainCouponId(e.target.value)}
              fullWidth
            >
              <MenuItem value="">{t('coupons.noneSelected')}</MenuItem>
              {nodes.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.code} — {c.name}
                </MenuItem>
              ))}
            </TextField>
            <FormHelperText>{t('coupons.mainCouponHint')}</FormHelperText>
          </Box>
          <Button
            variant="contained"
            onClick={handleSaveMainCoupon}
            disabled={savingMain || !mainCouponChanged}
          >
            {t('coupons.saveMainCoupon')}
          </Button>
        </Paper>
      )}

      <Box sx={{ px: { xs: 1, sm: 0 } }}>
        <DataTable<Row>
          enableMobileCards
          columns={columns}
          data={rows}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={rows.length}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          paginationMode="client"
          onRowClick={(row) => navigate(`/coupons/${row.id}`)}
        />
      </Box>

      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          resetForm();
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t('coupons.create')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('common.name')}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
            required
          />
          <TextField
            label={t('masterData.code')}
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            fullWidth
            required
          />
          <TextField
            select
            label={t('common.type')}
            value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as CouponType }))}
            fullWidth
          >
            {COUPON_TYPES.map((x) => (
              <MenuItem key={x} value={x}>
                {couponTypeLabel(t, x)}
              </MenuItem>
            ))}
          </TextField>
          {form.type === 'DURATION' && (
            <TextField
              label={t('coupons.duration')}
              value={form.duration}
              onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))}
              type="number"
              inputProps={{ min: 1 }}
              fullWidth
              required
            />
          )}
          <TextField
            select
            label={t('coupons.discountType')}
            value={form.discountType}
            onChange={(e) =>
              setForm((f) => ({ ...f, discountType: e.target.value as CouponDiscountType }))
            }
            fullWidth
          >
            {COUPON_DISCOUNT_TYPES.map((x) => (
              <MenuItem key={x} value={x}>
                {couponDiscountTypeLabel(t, x)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('coupons.discountValue')}
            value={form.discountValue}
            onChange={(e) => setForm((f) => ({ ...f, discountValue: e.target.value }))}
            type="number"
            inputProps={{ min: 0, step: '0.01' }}
            fullWidth
            required
          />
          <TextField
            select
            label={t('common.status')}
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as CouponStatus }))}
            fullWidth
          >
            {COUPON_STATUSES.map((x) => (
              <MenuItem key={x} value={x}>
                {couponStatusLabel(t, x)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('coupons.usageType')}
            value={form.usageType}
            onChange={(e) =>
              setForm((f) => ({ ...f, usageType: e.target.value as CouponUsageType }))
            }
            fullWidth
            helperText={t('coupons.usageTypeHint')}
          >
            {COUPON_USAGE_TYPES.map((x) => (
              <MenuItem key={x} value={x}>
                {couponUsageTypeLabel(t, x)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('common.description')}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label={t('coupons.maxRedemptions')}
            value={form.maxRedemptions}
            onChange={(e) => setForm((f) => ({ ...f, maxRedemptions: e.target.value }))}
            type="number"
            inputProps={{ min: 1 }}
            fullWidth
          />
          <TextField
            label={t('coupons.expiryDate')}
            value={form.expiryDate}
            onChange={(e) => setForm((f) => ({ ...f, expiryDate: e.target.value }))}
            type="datetime-local"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={t('coupons.allowedPlanCodes')}
            value={form.allowedPlanCodes}
            onChange={(e) => setForm((f) => ({ ...f, allowedPlanCodes: e.target.value }))}
            fullWidth
            helperText={t('coupons.allowedPlanCodesHint')}
          />
          <TextField
            label={t('coupons.allowedProductSkus')}
            value={form.allowedProductSkus}
            onChange={(e) => setForm((f) => ({ ...f, allowedProductSkus: e.target.value }))}
            fullWidth
            helperText={t('coupons.allowedProductSkusHint')}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
              resetForm();
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !form.name.trim() || !form.code.trim()}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
